/**
 * Help center and FAQ texts (EN = source language).
 * FAQ answers use the same markup as the guides. Blank lines separate paragraphs.
 */
const faq = {
    help: {
        title: 'Help & guides',
        subtitle: 'Answers for setting up, unlocking and using the watchfaces and apps.',
        searchPlaceholder: 'Search, e.g. “weather”, “key”, “GPS?”',
        searchLabel: 'Search help',
        symptomsTitle: 'What does your watch show?',
        symptoms: {
            noData: 'Weather field stays empty',
            waiting: 'Weather or tides do not load',
            key: 'A feature is locked',
            gps: 'The position is missing',
            na: 'A data field shows nothing',
            zero: 'WatchfaceID shows 0',
            settings: 'Settings do not open or save',
            install: 'Installation is stuck',
        },
        settingsSymptom: 'Settings',
        installSymptom: 'Queue',
        guidesTitle: 'Guides',
        faqTitle: 'Frequently asked questions',
        faqAll: 'All',
        results: 'Results for “{{query}}”',
        resultsGuides: 'Guides',
        resultsFaq: 'Questions',
        resultsProducts: 'Watchfaces & apps',
        noResults: 'Nothing found for “{{query}}”.',
        noResultsHint: 'Try another word, or browse the guides below.',
        contact: 'Still stuck? Write via the [link=/contact]contact form[/link].',
        clear: 'Clear search',
        copyLink: 'Copy link to this question',
        linkCopied: 'Link copied',
        categories: {
            start: 'Installation & settings',
            purchase: 'Buying & unlocking',
            data: 'Weather, tides & GPS',
            display: 'Display & controls',
            privacy: 'Privacy & permissions',
        },
    },
    faqItems: {
        'install-fails': {
            q: 'The installation always fails or is stuck in the queue',
            a: 'This is caused by the Connect IQ app or Garmin’s servers, not by the watchface.\n\nRestart the watch, close the Connect IQ app completely and try again. Keep the phone screen on and the app open during the transfer. If it still fails, try again later. These problems usually disappear on their own. [link=/guides/install]Installation guide[/link]',
        },
        'not-showing': {
            q: 'The watchface is installed but not shown on my watch',
            a: 'It is probably not selected yet. Hold [ui]MENU[/ui], open [ui]Watch Face[/ui], scroll to the watchface and confirm. [link=/guides/install]Installation guide[/link]',
        },
        'settings-open': {
            q: 'How do I open the watchface settings?',
            a: 'Connect the watch via Bluetooth, open the [b]Connect IQ[/b] app, go to the watchface and tap [ui]Settings[/ui]. [link=/guides/settings]Settings guide[/link]',
        },
        'settings-wont-open': {
            q: 'The settings do not open or load slowly',
            a: 'The settings are stored on Garmin’s servers. If they are slow, wait a while and try again. Close the Connect IQ app completely and restart the watch if needed.\n\nIf you only see [ui]Install[/ui] instead of [ui]Settings[/ui], the watchface is not installed on this watch yet.',
        },
        'settings-cant-save': {
            q: 'The settings cannot be saved and an error appears',
            a: 'Garmin’s servers sometimes reject certain characters. Clear the theme string field (if there is one) and save again.',
        },
        'missing-option': {
            q: 'I cannot find a specific option',
            a: 'The available options depend on the watch model and the watchface. Some options only exist on newer watches or only on AMOLED or MIP displays.',
        },
        units: {
            q: 'How do I change units or the 12/24-hour format?',
            a: 'Temperature, distance, height and the time format follow the system settings of the watch: hold [ui]MENU[/ui] and open [ui]System[/ui] › [ui]Format[/ui].\n\nPressure and wind speed are set in the watchface settings ([set]Pressure Unit[/set], [set]Windspeed Unit[/set]).',
        },
        'vx-elite': {
            q: 'What is the difference between VX and Elite?',
            a: 'Both have the same features. VX runs on all supported watches, has a 5-day trial and is unlocked with a key from this website; the key is tied to one watch. Elite is bought in the Connect IQ Store with Garmin Pay, is tied to your Garmin account and needs no key. [link=/guides/purchase]VX or Elite?[/link]',
        },
        'pro-crown': {
            q: 'What does ♛ mean and why does a field show “key?”',
            a: '♛ marks PRO options. They work during the 5-day trial. After the trial, watchfaces with a VX and an Elite version show a notice on the display. All other watchfaces show [watch]key?[/watch] in PRO fields, or the PRO option stops working. Everything else keeps working. Unlock the watchface or choose a free option. [link=/guides/purchase]Buy & unlock[/link]',
        },
        permanent: {
            q: 'Is this a subscription?',
            a: 'No. A key is a one-time purchase and stays valid as long as you use the same watch. A new watch needs a new key.',
        },
        'missing-key': {
            q: 'I paid but did not receive a key',
            a: 'Check your spam folder first. Key emails often end up there.\n\nIf it is not there, the email address may contain a typo. Write via the [link=/contact]contact form[/link] and include the email address you used and your WatchfaceID.\n\nElite purchases with Garmin Pay never send a key. They are unlocked right away.',
        },
        'key-not-accepted': {
            q: 'My key is not accepted',
            a: 'The key only works for the watchface and the WatchfaceID it was bought for. A VX key does not unlock the Elite version. Tap [ui]Save[/ui] after entering it and wait until the settings reach the watch. Newer watchfaces show the [b]Lock Status[/b] on the watch. [link=/guides/find-watchface-id]Check it[/link]',
        },
        'watchfaceid-zero': {
            q: 'My WatchfaceID shows 0',
            a: 'The watch has not created the ID yet. Select the watchface on the watch, wait a minute and open the settings again. Do not buy a key for 0. If it stays 0, write via the [link=/contact]contact form[/link].',
        },
        'new-device': {
            q: 'I have a new watch and my old key does not work',
            a: 'A key is tied to one watch, and the new watch has a different WatchfaceID. If Garmin replaced your watch under warranty, write via the [link=/contact]contact form[/link] with the old and the new WatchfaceID.',
        },
        'double-payment': {
            q: 'I was charged twice',
            a: 'Write via the [link=/contact]contact form[/link]. The duplicate payment is refunded. It can take a few days until the money is back on your account.',
        },
        'gps-fix': {
            q: 'What is a GPS fix and why does it show “GPS?”',
            a: 'The watchface needs your position for sunrise, sunset, moon times, weather and tides. [watch]GPS?[/watch] means it has none yet.\n\nOpen the start screen of an activity with GPS, for example [ui]Run[/ui], and wait until the watch has a signal. You do not need to start the activity. [link=/guides/location]GPS & location[/link]',
        },
        'location-update': {
            q: 'How do I update the displayed location?',
            a: 'Do a GPS fix at the new place. The stored position only changes after you have moved more than about 1 km. With Garmin weather, the place name is the nearest weather station, often an airport. [link=/guides/location]GPS & location[/link]',
        },
        'weather-no-data': {
            q: 'The weather shows “no data” or “waiting for data”',
            a: 'The watch has not received weather data yet. Check that the watch is connected to the phone and the Garmin Connect app runs in the background.\n\nWith Garmin weather, check the Garmin weather widget on the watch: if it is empty, the watchface cannot show data either. Try Yr or Open-Meteo as provider, they need no key. [link=/guides/weather]Weather guide[/link]',
        },
        'tides-no-data': {
            q: 'Tide data does not appear',
            a: 'Check that the Stormglass key was copied completely (more than 60 characters), wait 30 minutes after saving and do a GPS fix. Far from the coast, enter alternative coordinates. [link=/guides/tides]Tides guide[/link]',
        },
        na: {
            q: 'A data field shows only “n/a”',
            a: 'Your watch does not provide this value, usually because a sensor is missing (for example barometer or pulse oximeter) or the model is older. Choose another option for this field.',
        },
        seconds: {
            q: 'Why do the seconds disappear after a short time?',
            a: 'After about 10 seconds the watch switches to low-power mode and updates the display only once a minute. Raise your wrist to see the seconds again. Showing seconds permanently costs a lot more battery.',
        },
        'second-gesture': {
            q: 'How does the second wrist movement work?',
            a: 'Raise your wrist to wake the watch, lower it and raise it again within about 7 seconds. The switchview areas then show their second view. [link=/guides/switchviews]Switchviews & gestures[/link]',
        },
        'view-keeps-changing': {
            q: 'The display keeps changing to different data',
            a: 'That is the switchview feature. If you do not want it, set view 2 to [b]Do not switch[/b] or to the same content as view 1.',
        },
        'activity-distance': {
            q: 'Weekly or monthly activity distances are wrong',
            a: 'Watchfaces can only count activities that were recorded on this watch. Garmin provides the weekly distance separately, so it can be correct while month values miss activities from other devices.',
        },
        'hour-minute-bar': {
            q: 'How does the hour/minute bar work?',
            a: 'Some watchfaces fill the hour and minute digits with a color, depending on an option you choose. Pick a fill color that differs from the digit background, otherwise you see nothing.',
        },
        'beer-symbol': {
            q: 'What does the beer symbol mean?',
            a: 'It shows how many beers (0.5 l each) you have “earned” today through your calorie burn. There is also a wine glass option.',
        },
        fonts: {
            q: 'Where can I see all fonts?',
            a: 'All time and data fonts are shown in the [link=/guides/fonts]fonts guide[/link]. Which ones a watchface offers depends on the watchface.',
        },
        'user-data': {
            q: 'What data do the watchfaces send?',
            a: 'Watchfaces cannot read personal data such as your name or contacts. Garmin does not give them access to it.\n\nTo load weather, tide and air quality data and to check the unlock key, watchfaces with these features send a request in the background to the TitanicTurtle server (orcapi.orcatec.net). It contains the stored position (latitude and longitude), the WatchfaceID, the watch model, the language, the watchface version, the ProKey and the API keys you entered. With Garmin weather as provider, this request runs about every 12 hours, otherwise at the refresh rate you set.',
        },
        permissions: {
            q: 'Why do the watchfaces need so many permissions?',
            a: 'Garmin asks for all permissions at installation. They cannot be granted one by one.\n\n[b]Run in the background[/b] and [b]Internet[/b]: to update weather and tide data.\n\n[b]Data from other apps[/b]: to show values that apps such as Body Battery, Stress or Atmos Air Quality explicitly provide.\n\n[b]GPS location[/b]: for sun and moon times and the weather location.\n\n[b]Heart rate, barometer, temperature and altitude history[/b]: only to display them.\n\n[b]Garmin Connect fitness profile[/b]: for weight, active calories and the sleep times used by night settings.',
        },
    },
};
export default faq;
